// frontend/apps/widgets/src/widgets/GiftVoucher/components/GiftVoucherCard.js
import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Card,
  CardMedia,
  Typography,
  Button,
  Chip,
  alpha,
} from '@mui/material';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import FavoriteIcon from '@mui/icons-material/Favorite';
import {fCurrency} from 'packages/common/utils/formatNumber';
import {getContrastTextColor} from 'packages/common/utils/colorUtils';

// Constantes pour la longueur des textes
const TEXT_LENGTHS = {
  TITLE: 80,
  DESCRIPTION: 150,
};

// Utilitaires de formatage de texte
const truncateText = (text, maxLength, endChar = '...') => {
  if (!text) return '';
  if (text.length <= maxLength) return text;

  const truncated = text.substr(0, maxLength);
  const lastSpace = truncated.lastIndexOf(' ');

  return `${truncated.substr(0, lastSpace)}${endChar}`;
};

const stripAndTruncate = (html, maxLength) => {
  if (!html) return '';
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  const text = tmp.textContent || tmp.innerText || '';
  return truncateText(text, maxLength);
};

const GiftVoucherCard = ({companyDetails, voucher, color, categories}) => {
  const cdnEndpoint = process.env.REACT_APP_SPACES_CDN_ENDPOINT || 'https://files.brydge.app';

  const getImageUrl = (voucher) => {
    // Essayer d'abord display.imageUrl (nouveau format du mapper)
    let imagePath = voucher.display?.imageUrl || voucher.imagePath;

    // Si l'image est déjà une URL complète, la retourner
    if (imagePath?.startsWith('http://') || imagePath?.startsWith('https://')) {
      return imagePath;
    }

    // Si pas d'image définie, utiliser l'image par défaut
    if (!imagePath) {
      imagePath = 'assets/images/giftVouchersTypes/default/giftvoucher_default.webp';
    }

    // Nettoyer le chemin pour éviter les doubles slashes
    const cleanPath = imagePath.startsWith('/') ? imagePath.slice(1) : imagePath;

    return `${cdnEndpoint}/${cleanPath}`;
  };

  const handlePurchaseClick = () => {
    const appBaseUrl = process.env.REACT_APP_PUBLIC_URL || 'https://go.brydge.app';
    // Directly navigate to the purchase page
    if (companyDetails && voucher) {
      // Utiliser voucher.id (nouveau format) ou voucher._id (ancien format)
      const voucherId = voucher.id || voucher._id;
      const purchaseUrl = `${appBaseUrl}/${companyDetails.slug}/buy/${voucherId}`;
      // Open in new tab
      window.open(purchaseUrl, '_blank');
    }
  };

  // Formatage du titre et de la description
  const formattedTitle = truncateText(voucher.name, TEXT_LENGTHS.TITLE);
  const formattedDescription = stripAndTruncate(
      voucher.shortDescription || voucher.description,
      TEXT_LENGTHS.DESCRIPTION,
  );

  // Prix formatté - gérer les deux formats de données
  const formattedPrice = (() => {
    // Nouveau format (après mapper)
    if (voucher.amount) {
      // Si le prix est de type fixe
      if (voucher.amount.type === 'fixed') {
        return fCurrency(voucher.amount.value);
      }

      // Si le prix est de type variable (avec min et max)
      if (voucher.amount.type === 'variable' && voucher.amount.min && voucher.amount.max) {
        return `${fCurrency(voucher.amount.min)} - ${fCurrency(voucher.amount.max)}`;
      }

      // Si le prix est de type variant avec des options
      if (voucher.amount.type === 'variant' && voucher.amount.variants?.length > 0) {
        const minAmount = Math.min(...voucher.amount.variants.map((v) => v.amount));
        return `À partir de ${fCurrency(minAmount)}`;
      }

      // Prix libre ou non défini
      return 'Montant Libre';
    }

    // Ancien format (compatibilité)
    if (voucher.amountType === 'fixed') {
      return fCurrency(voucher.fixedAmount);
    }

    if (voucher.amountType === 'variable' && voucher.minAmount && voucher.maxAmount) {
      return `${fCurrency(voucher.minAmount)} - ${fCurrency(voucher.maxAmount)}`;
    }

    if (voucher.amountType === 'variant' && voucher.variants?.length > 0) {
      const minAmount = Math.min(...voucher.variants.map((v) => v.fixedAmount));
      return `À partir de ${fCurrency(minAmount)}`;
    }

    return 'Montant Libre';
  })();

  const CategoryBadge = ({category}) => {
    if (!category) return null;

    return (
      <Box
        sx={{
          bgcolor: '#FFFFFF',
          color: color,
          py: 0.5,
          px: 2,
          borderRadius: '10px',
          fontSize: '0.875rem',
          fontWeight: 600,
          backdropFilter: 'blur(8px)',
          border: `1px solid ${alpha('#FFFFFF', 0.2)}`,
          position: 'absolute',
          top: 16,
          left: 16,
          zIndex: 2,
        }}
      >
        {category.name}
      </Box>
    );
  };

  // Find first category for display - gérer les deux formats
  const displayCategory = React.useMemo(() => {
    const voucherCategories = voucher.categories || [];
    if (!categories || voucherCategories.length === 0) {
      return null;
    }
    return categories.find((cat) => cat._id === voucherCategories[0] || cat.id === voucherCategories[0]);
  }, [voucher.categories, categories]);

  // Gérer isHighlighted dans les deux formats
  const isHighlighted = voucher.display?.isHighlighted || voucher.isHighlighted;

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        cursor: 'pointer',
      }}
      onClick={handlePurchaseClick}
    >
      {/* Badge Coup de Coeur */}
      {isHighlighted && (
        <Box
          sx={{
            'position': 'absolute',
            'top': -12,
            'right': -12,
            'width': 48,
            'height': 48,
            'borderRadius': '50%',
            'bgcolor': 'white',
            'display': 'flex',
            'alignItems': 'center',
            'justifyContent': 'center',
            'boxShadow': `0 4px 12px ${alpha(color, 0.2)}`,
            'zIndex': 3,
            'backdropFilter': 'blur(8px)',
            'border': `2px solid ${alpha(color, 0.1)}`,
            'animation': 'float 3s ease-in-out infinite',
            '@keyframes float': {
              '0%': {transform: 'translateY(0px)'},
              '50%': {transform: 'translateY(-4px)'},
              '100%': {transform: 'translateY(0px)'},
            },
          }}
        >
          <FavoriteIcon
            sx={{
              'color': 'error.main',
              'fontSize': 28,
              'animation': 'pulse 1.5s infinite',
              '@keyframes pulse': {
                '0%': {transform: 'scale(1)'},
                '50%': {transform: 'scale(1.15)'},
                '100%': {transform: 'scale(1)'},
              },
            }}
          />
        </Box>
      )}

      <Card
        sx={{
          'width': '100%',
          'height': 'auto',
          'borderRadius': 3,
          'bgcolor': 'background.paper',
          'position': 'relative',
          'overflow': 'hidden',
          'boxShadow': `0 8px 24px ${alpha(color, 0.15)}`,
          'transition': 'all 0.3s ease',
          'display': 'flex',
          'flexDirection': 'column',
          '&:hover': {
            transform: 'translateY(-8px)',
            boxShadow: `0 12px 32px ${alpha(color, 0.25)}`,
          },
        }}
      >
        {/* Section Image + Badges */}
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            paddingTop: '56.25%',
            flexShrink: 0,
          }}
        >
          <CardMedia
            component="img"
            image={getImageUrl(voucher)}
            alt={voucher.name}
            onError={(e) => {
              // Fallback en cas d'erreur de chargement
              if (process.env.REACT_APP_ENV === 'development') {
                console.error('🖼️ Erreur chargement image:', e.target.src);
              }
              e.target.src = `${cdnEndpoint}/assets/images/giftVouchersTypes/default/giftvoucher_default.webp`;
            }}
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              borderRadius: '12px 12px 0 0',
            }}
          />

          {/* Overlay gradient */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: `linear-gradient(to bottom, 
                transparent 0%, 
                ${alpha('#000', 0.2)} 70%, 
                ${alpha('#000', 0.6)} 100%
              )`,
              zIndex: 1,
            }}
          />

          {/* Badge Promo */}
          {voucher.promoPercentage && (
            <Chip
              label={`-${voucher.promoPercentage}%`}
              sx={{
                position: 'absolute',
                top: 16,
                right: 16,
                bgcolor: 'error.main',
                color: 'white',
                fontWeight: 'bold',
                zIndex: 2,
              }}
            />
          )}

          {/* Category Badge */}
          {displayCategory && <CategoryBadge category={displayCategory} />}

          {/* Badge Prix */}
          <Box
            sx={{
              position: 'absolute',
              bottom: -16,
              right: 16,
              zIndex: 2,
              bgcolor: color,
              color: getContrastTextColor(color),
              py: 1,
              px: 2,
              borderRadius: 2,
              boxShadow: `0 4px 12px ${alpha(color, 0.3)}`,
            }}
          >
            <Typography variant="body2">
              {formattedPrice}
            </Typography>
          </Box>
        </Box>

        {/* Section Contenu */}
        <Box
          sx={{
            p: 2,
            pt: 4,
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
          }}
        >
          {/* Titre et Description */}
          <Box sx={{flex: 1}}>
            <Box
              sx={{
                'height': '2.4em',
                'marginBottom': 1.5,
                'position': 'relative',
                'display': 'flex',
                'alignItems': 'center',
                'paddingBottom': '20px',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  width: 60,
                  height: '2px',
                  background: `linear-gradient(90deg, ${color} 0%, ${alpha(color, 0.2)} 100%)`,
                  transition: 'width 0.3s ease',
                },
                '&:hover::after': {
                  width: 100,
                },
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  lineHeight: 1.2,
                  letterSpacing: '0.02em',
                  color: 'text.primary',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  width: '100%',
                }}
                title={voucher.name}
              >
                {formattedTitle}
              </Typography>
            </Box>

            <Box
              sx={{
                'height': '3em',
                'display': 'flex',
                'alignItems': 'center',
                'position': 'relative',
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  lineHeight: 1.5,
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  width: '100%',
                }}
                title={stripAndTruncate(voucher.shortDescription || voucher.description, 500)}
              >
                {formattedDescription}
              </Typography>
            </Box>
          </Box>

          {/* Bouton d'achat */}
          <Button
            fullWidth
            size="medium"
            startIcon={<CardGiftcardIcon />}
            sx={{
              'bgcolor': color,
              'color': getContrastTextColor(color),
              'py': 1,
              'mt': 2,
              'textTransform': 'none',
              'borderRadius': 2,
              'fontWeight': 600,
              'fontSize': '0.9rem',
              'boxShadow': `0 4px 12px ${alpha(color, 0.2)}`,
              'transition': 'all 0.2s ease',
              '&:hover': {
                bgcolor: alpha(color, 0.9),
                transform: 'translateY(-2px)',
                boxShadow: `0 6px 16px ${alpha(color, 0.3)}`,
              },
            }}
            onClick={handlePurchaseClick}
          >
            Offrir
          </Button>
        </Box>
      </Card>
    </Box>
  );
};

GiftVoucherCard.propTypes = {
  companyDetails: PropTypes.object.isRequired,
  voucher: PropTypes.object.isRequired,
  color: PropTypes.string.isRequired,
  categories: PropTypes.array,
};

export default GiftVoucherCard;
